import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiService from '../../../../services/ApiService';
import type { Natureza } from '../../SteelOrders/AddOrder/Hooks/useTaxProjection';
import { InvoiceItem } from './types';

interface CfopOption {
  id: number;
  code: string;
  description: string;
}

interface Params {
  invoiceId: number | null;
  pedidoTaxConfigId: number | null;
  items: InvoiceItem[];
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>;
  cfops: CfopOption[];
  destStateId: number | null;
  companyEmissorId: number | null;
  isEmitida: boolean;
  naturezaOperacaoAtual: string;
  onSugerirNaturezaOperacao: (naturezaOperacao: string) => void;
}

// Sugestão, não trava: venda (x1xx) e retorno de industrialização; outros CFOPs não mexem no campo.
const sugerirNaturezaOperacao = (cfop: string | null): string | null => {
  if (!cfop) return null;
  if (/^[567]1\d\d$/.test(cfop)) return 'VENDA_MERCADORIA';
  if (cfop === '5902' || cfop === '6902') return 'RETORNO';
  return null;
};

export function useCenarioFiscalDosItens({
  invoiceId,
  pedidoTaxConfigId,
  items,
  setItems,
  cfops,
  destStateId,
  companyEmissorId,
  isEmitida,
  naturezaOperacaoAtual,
  onSugerirNaturezaOperacao,
}: Params) {
  const [resposta, setResposta] = useState<{ chave: string; lista: Natureza[] }>({ chave: '', lista: [] });
  const [cenarioTopoId, setCenarioTopoId] = useState<number | null>(null);
  const [personalizados, setPersonalizados] = useState<Set<number>>(new Set());
  const inicializadoPara = useRef<string | null>(null);
  const [inicializadoChave, setInicializadoChave] = useState<string | null>(null);

  const ncmKey = useMemo(
    () =>
      Array.from(new Set(items.map((i) => i.ncmId).filter((n): n is number => n != null)))
        .sort((a, b) => a - b)
        .join(','),
    [items],
  );

  // A lista só vale para a consulta que a trouxe: trocar emissor/UF não pode inicializar com a anterior.
  const chaveConsulta = `${ncmKey}|${destStateId}|${companyEmissorId}`;
  const naturezas = useMemo(
    () => (resposta.chave === chaveConsulta ? resposta.lista : []),
    [resposta, chaveConsulta],
  );

  useEffect(() => {
    if (isEmitida || !ncmKey || !destStateId || !companyEmissorId) return;
    let ativo = true;
    ApiService.fiscal
      .getNaturezas({ ncmIds: ncmKey, stateId: destStateId, companyEmissorId })
      .then((res) => {
        if (!ativo) return;
        const data = res.data?.naturezas ?? res.data?.data?.naturezas ?? [];
        setResposta({ chave: chaveConsulta, lista: Array.isArray(data) ? data : [] });
      })
      .catch(() => {
        if (ativo) setResposta({ chave: chaveConsulta, lista: [] });
      });
    return () => {
      ativo = false;
    };
  }, [isEmitida, ncmKey, destStateId, companyEmissorId, chaveConsulta]);

  const consultaConcluida = !ncmKey || !destStateId || !companyEmissorId || resposta.chave === chaveConsulta;
  const pronto =
    isEmitida ||
    (consultaConcluida &&
      (naturezas.length === 0 || inicializadoChave === `${invoiceId}-${companyEmissorId}-${destStateId}`));

  const cenarioTopo = naturezas.find((n) => n.taxConfigId === cenarioTopoId) ?? null;

  const aplicar = useCallback(
    (natureza: Natureza, pular: Set<number>) => {
      const cfop = natureza.cfop;
      if (!cfop) return;
      const cfopId = cfops.find((c) => String(c.code) === cfop)?.id ?? null;
      setItems((prev) =>
        prev.map((p) =>
          !pular.has(p.id) && p.ncmId != null && p.cfop !== cfop
            ? { ...p, cfop, cfopId }
            : p,
        ),
      );
    },
    [cfops, setItems],
  );

  // Uma vez por nota/emissor/UF: topo = natureza do pedido, senão a única que cobre todos os NCMs.
  useEffect(() => {
    if (isEmitida || !invoiceId || naturezas.length === 0 || cfops.length === 0 || items.length === 0) return;
    const chave = `${invoiceId}-${companyEmissorId}-${destStateId}`;
    if (inicializadoPara.current === chave) return;
    inicializadoPara.current = chave;
    setInicializadoChave(chave);

    const completas = naturezas.filter((n) => n.ncmsDescobertos.length === 0);
    const inicial =
      naturezas.find((n) => n.taxConfigId === pedidoTaxConfigId) ?? (completas.length === 1 ? completas[0] : null);
    setCenarioTopoId(inicial?.taxConfigId ?? null);
    if (!inicial?.cfop) {
      setPersonalizados(new Set());
      return;
    }
    // Ao reabrir, linha coberta pelo topo com outro CFOP já gravado volta marcada.
    const marcadas = new Set(
      items
        .filter((i) => i.cfop && i.ncmId != null && inicial.ncmsCobertos.includes(i.ncmId) && i.cfop !== inicial.cfop)
        .map((i) => i.id),
    );
    setPersonalizados(marcadas);
    aplicar(inicial, marcadas);
    const sugestao = sugerirNaturezaOperacao(inicial.cfop);
    if (sugestao && !naturezaOperacaoAtual) onSugerirNaturezaOperacao(sugestao);
  }, [
    isEmitida, invoiceId, naturezas, cfops, items, companyEmissorId, destStateId,
    pedidoTaxConfigId, aplicar, naturezaOperacaoAtual, onSugerirNaturezaOperacao,
  ]);

  // Item novo na nota (Adicionar item) entra com o CFOP do topo.
  useEffect(() => {
    if (isEmitida || !cenarioTopo) return;
    const pendente = items.some(
      (i) => !i.cfop && !personalizados.has(i.id) && i.ncmId != null,
    );
    if (pendente) aplicar(cenarioTopo, personalizados);
  }, [isEmitida, items, cenarioTopo, personalizados, aplicar]);

  const mudarCenarioTopo = (taxConfigId: number | null) => {
    setCenarioTopoId(taxConfigId);
    const natureza = naturezas.find((n) => n.taxConfigId === taxConfigId);
    if (!natureza) return;
    aplicar(natureza, personalizados);
    const sugestao = sugerirNaturezaOperacao(natureza.cfop);
    if (sugestao) onSugerirNaturezaOperacao(sugestao);
  };

  // Troca manual na linha: diferente do topo marca; igual ao topo desmarca.
  const marcarTrocaManual = (itemIds: number[], cfop: string) => {
    setPersonalizados((prev) => {
      const next = new Set(prev);
      itemIds.forEach((id) => (cenarioTopo?.cfop && cfop !== cenarioTopo.cfop ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const voltarAoTopo = (itemIds: number[]) => {
    const next = new Set(personalizados);
    itemIds.forEach((id) => next.delete(id));
    setPersonalizados(next);
    if (cenarioTopo) aplicar(cenarioTopo, next);
  };

  const nomePorCfop = useMemo(
    () => new Map(naturezas.filter((n) => n.cfop).map((n) => [n.cfop as string, n.nome ?? n.cfop ?? ''])),
    [naturezas],
  );

  const itensSemCenario = (natureza: Natureza) =>
    items.filter((i) => i.ncmId != null && natureza.ncmsDescobertos.includes(i.ncmId)).length;

  const semCenarioNoTopo = (ncmId: number | null) =>
    ncmId != null && !!cenarioTopo && cenarioTopo.ncmsDescobertos.includes(ncmId);

  return {
    pronto,
    naturezas,
    cenarioTopoId,
    mudarCenarioTopo,
    personalizados,
    marcarTrocaManual,
    voltarAoTopo,
    nomePorCfop,
    itensSemCenario,
    semCenarioNoTopo,
    nomeCenarioTopo: cenarioTopo?.nome ?? cenarioTopo?.cfop ?? null,
  };
}

export type CenarioFiscalDosItens = ReturnType<typeof useCenarioFiscalDosItens>;
